import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/shared/utils/cn';

/** Alto máximo del panel (max-h-60 de la lista + el padding del contenedor). */
const PANEL_MAX_HEIGHT = 248;
const PANEL_GAP = 4;
const PANEL_MIN_HEIGHT = 140;

type PanelPosition = {
  left: number;
  top?: number;
  bottom?: number;
  width: number;
  maxHeight: number;
};

export type SelectOption = {
  label: string;
  value: string;
};

type SelectProps = {
  id: string;
  value?: string;
  options?: SelectOption[];
  onValueChange?: (value: string) => void;
  onBlur?: () => void;
  hasError?: boolean;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  name?: string;
  'aria-describedby'?: string;
  'aria-label'?: string;
};

export function Select({
  id,
  value = '',
  options = [],
  onValueChange,
  onBlur,
  hasError,
  placeholder,
  disabled,
  className,
  name,
  'aria-describedby': ariaDescribedBy,
  'aria-label': ariaLabel,
}: SelectProps) {
  const generatedListboxId = useId();
  const listboxId = `${id || generatedListboxId}-listbox`;
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [panelPosition, setPanelPosition] = useState<PanelPosition | null>(null);

  // El panel se monta fuera del formulario (body, o el shadow root del Web
  // Component) y se posiciona fijo: así no lo recorta el contenedor con scroll
  // de un modal ni ningún `overflow` intermedio.
  const portalContainer = useMemo(() => {
    if (!isOpen || typeof document === 'undefined') return null;

    const root = rootRef.current?.getRootNode();
    return root instanceof ShadowRoot ? root : document.body;
  }, [isOpen]);

  const updatePanelPosition = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom - PANEL_GAP * 2;
    const spaceAbove = rect.top - PANEL_GAP * 2;
    const opensUp = spaceBelow < PANEL_MIN_HEIGHT && spaceAbove > spaceBelow;
    const available = opensUp ? spaceAbove : spaceBelow;

    setPanelPosition({
      left: rect.left,
      width: rect.width,
      maxHeight: Math.max(PANEL_MIN_HEIGHT, Math.min(PANEL_MAX_HEIGHT, available)),
      ...(opensUp
        ? { bottom: window.innerHeight - rect.top + PANEL_GAP }
        : { top: rect.bottom + PANEL_GAP }),
    });
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;

    updatePanelPosition();

    // `capture` para enterarse también del scroll del contenedor del modal,
    // que no burbujea hasta window.
    window.addEventListener('scroll', updatePanelPosition, true);
    window.addEventListener('resize', updatePanelPosition);

    return () => {
      window.removeEventListener('scroll', updatePanelPosition, true);
      window.removeEventListener('resize', updatePanelPosition);
    };
  }, [isOpen, updatePanelPosition]);

  const selectedOption = useMemo(
    () => options.find((option) => option.value === value),
    [options, value],
  );
  useEffect(() => {
    if (!isOpen) return undefined;

    const selectedIndex = options.findIndex((option) => option.value === value);
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);

    const handlePointerDown = (event: PointerEvent) => {
      // event.composedPath() traverses shadow DOM boundaries; event.target is
      // retargeted to the shadow host when crossing the shadow boundary, so
      // contains() would always return false inside Shadow DOM.
      const path = event.composedPath();
      if (!path.includes(rootRef.current!) && !(panelRef.current && path.includes(panelRef.current))) {
        setIsOpen(false);
        onBlur?.();
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
        onBlur?.();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onBlur, options, value]);

  const selectOption = (option: SelectOption) => {
    onValueChange?.(option.value);
    setIsOpen(false);
    onBlur?.();
    triggerRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (isOpen && options[activeIndex]) {
        selectOption(options[activeIndex]);
        return;
      }

      setIsOpen(true);
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
        return;
      }

      const direction = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((current) => {
        const next = current + direction;
        if (next < 0) return options.length - 1;
        if (next >= options.length) return 0;
        return next;
      });
    }
  };

  return (
    <div ref={rootRef} className={cn('relative', className)}>
      <input type="hidden" name={name} value={value} />
      <button
        ref={triggerRef}
        id={id}
        type="button"
        disabled={disabled}
        aria-describedby={ariaDescribedBy}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-controls={listboxId}
        aria-label={ariaLabel}
        className={cn(
          'flex w-full items-center justify-between gap-2 rounded-adipa-md border bg-adipa-surface px-3 py-2',
          'text-left text-sm font-normal text-adipa-text-primary',
          'transition-colors duration-adipa-fast outline-none',
          isOpen ? 'border-adipa-primary ring-[3px] ring-adipa-focus-ring' : 'border-adipa-border hover:border-adipa-border-strong',
          hasError && 'border-adipa-error focus:border-adipa-error focus:ring-adipa-error-ring',
          disabled && 'cursor-not-allowed bg-adipa-surface-muted text-adipa-text-secondary opacity-70',
        )}
        onClick={() => {
          if (!disabled) setIsOpen((current) => !current);
        }}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (!isOpen) onBlur?.();
        }}
      >
        <span className={cn('min-w-0 truncate', !selectedOption && 'text-adipa-text-placeholder')}>
          {selectedOption?.label ?? placeholder ?? 'Selecciona una opción'}
        </span>
        <span
          aria-hidden="true"
          className={cn(
            'shrink-0 text-adipa-primary transition-transform duration-adipa-fast',
            isOpen && 'rotate-180',
          )}
        >
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="m6 9 6 6 6-6" />
          </svg>
        </span>
      </button>

      {isOpen && !disabled && portalContainer
        ? createPortal(
        <div
          ref={panelRef}
          data-adipa-select-panel=""
          className="fixed overflow-hidden rounded-adipa-md border border-adipa-border bg-adipa-surface shadow-adipa-console-panel"
          style={{
            left: panelPosition?.left,
            top: panelPosition?.top,
            bottom: panelPosition?.bottom,
            width: panelPosition?.width || undefined,
            zIndex: 10001,
          }}
        >
          <ul
            id={listboxId}
            role="listbox"
            aria-labelledby={id}
            className="overflow-y-auto p-1"
            style={{ maxHeight: panelPosition?.maxHeight }}
          >
            {options.map((option, index) => {
              const isSelected = option.value === value;
              const isActive = index === activeIndex;

              return (
                <li key={option.value} role="presentation">
                  <button
                    id={`${listboxId}-${option.value}`}
                    role="option"
                    aria-selected={isSelected}
                    type="button"
                    className={cn(
                      'flex min-h-9 w-full items-center justify-between gap-2 rounded-adipa-sm px-2.5 text-left text-sm font-normal transition-colors duration-adipa-fast',
                      isSelected
                        ? 'bg-adipa-surface-lavender text-adipa-primary'
                        : 'text-adipa-text-primary hover:bg-adipa-surface-muted hover:text-adipa-primary',
                      isActive && !isSelected && 'bg-adipa-surface-muted text-adipa-primary',
                    )}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => selectOption(option)}
                  >
                    <span className="min-w-0 whitespace-normal break-words py-1.5 leading-snug">{option.label}</span>
                    {isSelected && (
                      <svg aria-hidden="true" className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m5 12 5 5L20 7" />
                      </svg>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>,
            portalContainer,
          )
        : null}
    </div>
  );
}
